/**
 * Batch-2 write dialogs (fieldiq-api#1144): a job, a crew member, a job's
 * cancellation, a weld, and an exam with its indications.
 *
 * Same rules as NdtDialogs.tsx: writes are snake_case, lengths go to the API
 * in the tenant's unit as typed (`meta.units.length` labels the input), and
 * every rule the API checks is the API's — a refusal arrives with a `code`
 * and is shown as a plain sentence (`ndtRefusal`, lib/ndt-inspection.ts).
 *
 * THE SHOT GUARD IS THE API'S. The exam dialog filters the instrument picker
 * to the kind the method needs (from the catalog) to help the hand pick, but
 * it refuses nothing itself: a 409 comes back with every failure listed, and a
 * successful save may carry warnings (things due soon), which are handed to
 * the caller to show.
 *
 * `examined_at` is typed on the TENANT's wall clock and sent as an instant
 * (lib/datetime-local.ts). Left untouched it is not sent, and the API stamps
 * its own now.
 */
import React, { useMemo, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  ndtPersonName,
  useAddNdtCrew,
  useAddNdtExam,
  useAddNdtWeld,
  useCreateNdtJob,
  useNdtCatalog,
  useNdtCustomers,
  useNdtInstruments,
  useNdtPeople,
  useNdtSources,
  useNdtWelders,
  useUpdateNdtJob,
  useUpdateNdtWeld,
} from '../../hooks/useNdtInspection';
import { fromDatetimeLocal, toDatetimeLocal } from '../../lib/datetime-local';
import {
  inputValue,
  NDT_EXAM_CYCLE_LABEL,
  NDT_TICKET_MODE_LABEL,
  ndtKindLabel,
  ndtRefusal,
  ndtWarningText,
  numberFromInput,
  type NdtRefusal,
  ndtLocationRefLabel,
} from '../../lib/ndt-inspection';
import type {
  NdtCatalog,
  NdtCatalogB2,
  NdtCrew,
  NdtExamWrite,
  NdtIndicationWrite,
  NdtJob,
  NdtJobWrite,
  NdtMeta,
  NdtUnits,
  NdtWeld,
  NdtWeldWrite,
} from '../../types/ndt-inspection';
import { NDT_DATE_INPUT, NDT_INPUT, NDT_SECONDARY, NdtDialog, NdtField, NdtFormError } from './NdtBits';
import { changedOnly, Footer, intFromInput, PeopleSelect, textOrNull, useForm, type Change } from './NdtDialogs';
import { NdtRefusalNotice } from './NdtJobBits';

type Catalog = (NdtCatalog & NdtCatalogB2) | null | undefined;

const lengthLabel = (base: string, units: NdtUnits | null | undefined) => (units?.length ? `${base} (${units.length})` : base);

function CatalogSelect({
  label,
  value,
  onChange,
  options,
  blank,
  required,
}: {
  label: string;
  value: string;
  onChange: (e: Change) => void;
  options: { value: string; label: string }[] | undefined;
  blank: string;
  required?: boolean;
}) {
  return (
    <NdtField label={label}>
      <select className={NDT_INPUT} value={value} onChange={onChange} required={required}>
        <option value="">{blank}</option>
        {(options ?? []).map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </NdtField>
  );
}

// ─── Job ─────────────────────────────────────────────────────────────────────

interface JobValues {
  customer_id: string;
  line_ref: string;
  project_ref: string;
  external_project_ref: string;
  external_source: string;
  po_number: string;
  afe_number: string;
  location_name: string;
  lsd: string;
  province_state: string;
  latitude: string;
  longitude: string;
  acceptance_code: string;
  ticket_mode: string;
  scheduled_date: string;
  scope: string;
}

/**
 * Create (no `job`) or edit a job. On create, a blank acceptance code and
 * ticket mode are not sent, so the API snapshots the company's NDT defaults.
 */
export function NdtJobDialog({
  open,
  onClose,
  job,
  meta,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  job?: NdtJob | null;
  meta?: NdtMeta | null;
  onCreated?: (id: string) => void;
}) {
  const create = useCreateNdtJob();
  const update = useUpdateNdtJob();
  const customers = useNdtCustomers(open);
  const catalog = useNdtCatalog();
  const cat = catalog.data?.data as Catalog;
  const [refusal, setRefusal] = useState<NdtRefusal | null>(null);
  const { v, set, error, setError, start } = useForm<JobValues>(open, () => ({
    customer_id: job?.customerId ?? '',
    line_ref: job?.lineRef ?? '',
    project_ref: job?.projectRef ?? '',
    external_project_ref: job?.externalProjectRef ?? '',
    external_source: job?.externalSource ?? '',
    po_number: job?.poNumber ?? '',
    afe_number: job?.afeNumber ?? '',
    location_name: job?.locationName ?? '',
    lsd: job?.lsd ?? '',
    province_state: job?.provinceState ?? '',
    latitude: inputValue(job?.latitude),
    longitude: inputValue(job?.longitude),
    acceptance_code: job?.acceptanceCode ?? '',
    ticket_mode: job?.ticketMode ?? '',
    scheduled_date: job?.scheduledDate ?? '',
    scope: job?.scope ?? '',
  }));

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setRefusal(null);
    if (!v.customer_id) return setError('Choose a customer.');
    const lat = numberFromInput(v.latitude);
    const lng = numberFromInput(v.longitude);
    if (Number.isNaN(lat) || Number.isNaN(lng)) return setError('Latitude and longitude must be numbers.');
    const full: NdtJobWrite = {
      customer_id: v.customer_id,
      line_ref: textOrNull(v.line_ref),
      project_ref: textOrNull(v.project_ref),
      external_project_ref: textOrNull(v.external_project_ref),
      external_source: textOrNull(v.external_source),
      po_number: textOrNull(v.po_number),
      afe_number: textOrNull(v.afe_number),
      location_name: textOrNull(v.location_name),
      lsd: textOrNull(v.lsd),
      province_state: textOrNull(v.province_state),
      latitude: lat,
      longitude: lng,
      acceptance_code: v.acceptance_code || null,
      ticket_mode: (v.ticket_mode || undefined) as NdtJobWrite['ticket_mode'],
      scheduled_date: v.scheduled_date || null,
      scope: textOrNull(v.scope),
    };
    const onError = (err: unknown) => setRefusal(ndtRefusal(err, 'The job could not be saved.'));
    if (job) {
      const body = changedOnly(full, v, start);
      if (Object.keys(body).length === 0) return onClose();
      update.mutate({ id: job.id, body }, { onSuccess: () => { toast.success('Job saved'); onClose(); }, onError });
    } else {
      const body: NdtJobWrite = { ...full };
      if (!v.acceptance_code) delete body.acceptance_code;
      if (!v.ticket_mode) delete body.ticket_mode;
      create.mutate(body, {
        onSuccess: (res) => {
          toast.success(res.data?.jobNumber ? `Job ${res.data.jobNumber} created` : 'Job created');
          onClose();
          if (res.data?.id) onCreated?.(res.data.id);
        },
        onError,
      });
    }
  };

  return (
    <NdtDialog
      open={open}
      title={job ? `Edit ${job.jobNumber}` : 'New NDT job'}
      onClose={onClose}
      wide
      footer={<Footer formId="ndt-job" pending={create.isPending || update.isPending} label="Save" onClose={onClose} />}
    >
      <form id="ndt-job" onSubmit={submit} className="space-y-4" noValidate>
        <NdtFormError message={error} />
        <NdtRefusalNotice refusal={refusal} />
        <div className="grid gap-3 sm:grid-cols-2">
          <NdtField label="Customer" hint={customers.isError ? 'Customers could not be loaded.' : undefined}>
            <select className={NDT_INPUT} value={v.customer_id} onChange={set('customer_id')} required>
              <option value="">{customers.isLoading ? 'Loading…' : 'Choose a customer'}</option>
              {(customers.data ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </NdtField>
          <NdtField label="Scheduled">
            <input type="date" className={NDT_DATE_INPUT} value={v.scheduled_date} onChange={set('scheduled_date')} />
          </NdtField>
          <NdtField label="Line">
            <input className={NDT_INPUT} value={v.line_ref} onChange={set('line_ref')} />
          </NdtField>
          <NdtField label="Project">
            <input className={NDT_INPUT} value={v.project_ref} onChange={set('project_ref')} />
          </NdtField>
          <NdtField label="Location">
            <input className={NDT_INPUT} value={v.location_name} onChange={set('location_name')} />
          </NdtField>
          <NdtField label={ndtLocationRefLabel(v.province_state, job?.currency ?? meta?.currency)}>
            <input className={NDT_INPUT} value={v.lsd} onChange={set('lsd')} />
          </NdtField>
          <NdtField label="Province / state" hint="Two letters, e.g. AB or TX.">
            <input className={NDT_INPUT} value={v.province_state} onChange={set('province_state')} maxLength={2} />
          </NdtField>
          <div className="grid grid-cols-2 gap-3">
            <NdtField label="Latitude">
              <input className={NDT_INPUT} inputMode="decimal" value={v.latitude} onChange={set('latitude')} />
            </NdtField>
            <NdtField label="Longitude">
              <input className={NDT_INPUT} inputMode="decimal" value={v.longitude} onChange={set('longitude')} />
            </NdtField>
          </div>
          <CatalogSelect
            label="Acceptance code"
            value={v.acceptance_code}
            onChange={set('acceptance_code')}
            options={cat?.codes}
            blank={job ? 'None' : 'Company default'}
          />
          <NdtField label="Field tickets">
            <select className={NDT_INPUT} value={v.ticket_mode} onChange={set('ticket_mode')}>
              {!job && <option value="">Company default</option>}
              {Object.entries(NDT_TICKET_MODE_LABEL).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </NdtField>
          <NdtField label="PO number">
            <input className={NDT_INPUT} value={v.po_number} onChange={set('po_number')} />
          </NdtField>
          <NdtField label="AFE">
            <input className={NDT_INPUT} value={v.afe_number} onChange={set('afe_number')} />
          </NdtField>
          <NdtField label="External project ref" hint="The pipeline system's id for this project, if any.">
            <input className={NDT_INPUT} value={v.external_project_ref} onChange={set('external_project_ref')} />
          </NdtField>
          <NdtField label="External source">
            <input className={NDT_INPUT} value={v.external_source} onChange={set('external_source')} />
          </NdtField>
        </div>
        <NdtField label="Scope">
          <textarea className={`${NDT_INPUT} h-20 py-2`} value={v.scope} onChange={set('scope')} />
        </NdtField>
        <p className="text-xs text-fieldiq-muted">
          Weld diameters and walls on this account are in {meta?.units?.length ?? '—'}.
        </p>
      </form>
    </NdtDialog>
  );
}

// ─── Cancel ──────────────────────────────────────────────────────────────────

export function NdtCancelJobDialog({ open, onClose, job }: { open: boolean; onClose: () => void; job: NdtJob }) {
  const update = useUpdateNdtJob();
  const [reason, setReason] = useState('');
  const [refusal, setRefusal] = useState<NdtRefusal | null>(null);
  const [error, setError] = useState<string | null>(null);
  React.useEffect(() => {
    if (open) {
      setReason('');
      setRefusal(null);
      setError(null);
    }
  }, [open]);
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setRefusal(null);
    if (!reason.trim()) return setError('Say why the job is cancelled.');
    setError(null);
    update.mutate(
      { id: job.id, body: { status: 'cancelled', cancel_reason: reason.trim() } },
      {
        onSuccess: () => {
          toast.success(`${job.jobNumber} cancelled`);
          onClose();
        },
        onError: (err) => setRefusal(ndtRefusal(err, 'The job could not be cancelled.')),
      },
    );
  };
  return (
    <NdtDialog
      open={open}
      title={`Cancel ${job.jobNumber}`}
      onClose={onClose}
      footer={<Footer formId="ndt-cancel-job" pending={update.isPending} label="Cancel job" onClose={onClose} />}
    >
      <form id="ndt-cancel-job" onSubmit={submit} className="space-y-3" noValidate>
        <NdtFormError message={error} />
        <NdtRefusalNotice refusal={refusal} />
        <p className="text-sm text-fieldiq-text">A cancelled job cannot be reopened or changed.</p>
        <NdtField label="Reason">
          <textarea className={`${NDT_INPUT} h-24 py-2`} value={reason} onChange={(e) => setReason(e.target.value)} />
        </NdtField>
      </form>
    </NdtDialog>
  );
}

// ─── Crew ────────────────────────────────────────────────────────────────────

interface CrewValues {
  user_id: string;
  crew_role: string;
  start_on: string;
  end_on: string;
  notes: string;
}

export function NdtCrewDialog({ open, onClose, jobId }: { open: boolean; onClose: () => void; jobId: string }) {
  const add = useAddNdtCrew();
  const catalog = useNdtCatalog();
  const cat = catalog.data?.data as Catalog;
  const [refusal, setRefusal] = useState<NdtRefusal | null>(null);
  const { v, set, error, setError } = useForm<CrewValues>(open, () => ({ user_id: '', crew_role: '', start_on: '', end_on: '', notes: '' }));
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setRefusal(null);
    if (!v.user_id) return setError('Choose a person.');
    if (!v.crew_role) return setError('Choose a crew role.');
    add.mutate(
      {
        jobId,
        body: { user_id: v.user_id, crew_role: v.crew_role, start_on: v.start_on || null, end_on: v.end_on || null, notes: textOrNull(v.notes) },
      },
      {
        onSuccess: () => {
          toast.success('Added to the crew');
          onClose();
        },
        onError: (err) => setRefusal(ndtRefusal(err, 'The crew member could not be added.')),
      },
    );
  };
  return (
    <NdtDialog
      open={open}
      title="Add to crew"
      onClose={onClose}
      footer={<Footer formId="ndt-crew" pending={add.isPending} label="Add" onClose={onClose} />}
    >
      <form id="ndt-crew" onSubmit={submit} className="space-y-4" noValidate>
        <NdtFormError message={error} />
        <NdtRefusalNotice refusal={refusal} />
        <div className="grid gap-3 sm:grid-cols-2">
          <PeopleSelect label="Person" value={v.user_id} onChange={set('user_id')} required />
          <CatalogSelect label="Crew role" value={v.crew_role} onChange={set('crew_role')} options={cat?.crewRoles} blank="Choose a role" required />
          <NdtField label="From">
            <input type="date" className={NDT_DATE_INPUT} value={v.start_on} onChange={set('start_on')} />
          </NdtField>
          <NdtField label="To">
            <input type="date" className={NDT_DATE_INPUT} value={v.end_on} onChange={set('end_on')} />
          </NdtField>
        </div>
        <NdtField label="Notes">
          <textarea className={`${NDT_INPUT} h-16 py-2`} value={v.notes} onChange={set('notes')} />
        </NdtField>
      </form>
    </NdtDialog>
  );
}

// ─── Weld ────────────────────────────────────────────────────────────────────

interface WeldValues {
  weld_number: string;
  line_ref: string;
  spool: string;
  station: string;
  kp: string;
  diameter: string;
  wall: string;
  material: string;
  process: string;
  position: string;
  welder_id: string;
  weld_date: string;
  joint_type: string;
  external_weld_ref: string;
  notes: string;
}

/** Create a weld on `jobId`, or edit `weld`. Diameter and wall in meta.units.length. */
export function NdtWeldDialog({
  open,
  onClose,
  jobId,
  weld,
  units,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  jobId: string;
  weld?: NdtWeld | null;
  units: NdtUnits | null | undefined;
  onCreated?: (id: string) => void;
}) {
  const add = useAddNdtWeld();
  const update = useUpdateNdtWeld();
  const catalog = useNdtCatalog();
  const welders = useNdtWelders();
  const cat = catalog.data?.data as Catalog;
  const [refusal, setRefusal] = useState<NdtRefusal | null>(null);
  const { v, set, error, setError, start } = useForm<WeldValues>(open, () => ({
    weld_number: weld?.weldNumber ?? '',
    line_ref: weld?.lineRef ?? '',
    spool: weld?.spool ?? '',
    station: weld?.station ?? '',
    kp: weld?.kp ?? '',
    diameter: inputValue(weld?.diameter),
    wall: inputValue(weld?.wall),
    material: weld?.material ?? '',
    process: weld?.process ?? '',
    position: weld?.position ?? '',
    welder_id: weld?.welderId ?? '',
    weld_date: weld?.weldDate ?? '',
    joint_type: weld?.jointType ?? '',
    external_weld_ref: weld?.externalWeldRef ?? '',
    notes: weld?.notes ?? '',
  }));

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setRefusal(null);
    if (!v.weld_number.trim()) return setError('Weld number is required.');
    const diameter = numberFromInput(v.diameter);
    const wall = numberFromInput(v.wall);
    if (Number.isNaN(diameter) || Number.isNaN(wall)) return setError('Diameter and wall must be numbers.');
    const full: NdtWeldWrite = {
      weld_number: v.weld_number.trim(),
      line_ref: textOrNull(v.line_ref),
      spool: textOrNull(v.spool),
      station: textOrNull(v.station),
      kp: textOrNull(v.kp),
      diameter,
      wall,
      material: textOrNull(v.material),
      process: v.process || null,
      position: v.position || null,
      welder_id: v.welder_id || null,
      weld_date: v.weld_date || null,
      joint_type: v.joint_type || null,
      external_weld_ref: textOrNull(v.external_weld_ref),
      notes: textOrNull(v.notes),
    };
    const onError = (err: unknown) => setRefusal(ndtRefusal(err, 'The weld could not be saved.'));
    if (weld) {
      const body = changedOnly(full, v, start);
      if (Object.keys(body).length === 0) return onClose();
      update.mutate({ id: weld.id, body }, { onSuccess: () => { toast.success('Weld saved'); onClose(); }, onError });
    } else {
      add.mutate(
        { jobId, body: full },
        {
          onSuccess: (res) => {
            toast.success(`Weld ${res.data?.weldNumber ?? ''} added`.trim());
            onClose();
            if (res.data?.id) onCreated?.(res.data.id);
          },
          onError,
        },
      );
    }
  };

  return (
    <NdtDialog
      open={open}
      title={weld ? `Edit weld ${weld.weldNumber}` : 'Add weld'}
      onClose={onClose}
      wide
      footer={<Footer formId="ndt-weld" pending={add.isPending || update.isPending} label="Save" onClose={onClose} />}
    >
      <form id="ndt-weld" onSubmit={submit} className="space-y-4" noValidate>
        <NdtFormError message={error} />
        <NdtRefusalNotice refusal={refusal} />
        <div className="grid gap-3 sm:grid-cols-3">
          <NdtField label="Weld number">
            <input className={NDT_INPUT} value={v.weld_number} onChange={set('weld_number')} required />
          </NdtField>
          <NdtField label="Weld date">
            <input type="date" className={NDT_DATE_INPUT} value={v.weld_date} onChange={set('weld_date')} />
          </NdtField>
          <CatalogSelect label="Joint" value={v.joint_type} onChange={set('joint_type')} options={cat?.jointTypes} blank="Not recorded" />
          <NdtField label="Line">
            <input className={NDT_INPUT} value={v.line_ref} onChange={set('line_ref')} />
          </NdtField>
          <NdtField label="Spool">
            <input className={NDT_INPUT} value={v.spool} onChange={set('spool')} />
          </NdtField>
          <NdtField label="Station / KP">
            <div className="grid grid-cols-2 gap-2">
              <input className={NDT_INPUT} aria-label="Station" value={v.station} onChange={set('station')} placeholder="Station" />
              <input className={NDT_INPUT} aria-label="KP" value={v.kp} onChange={set('kp')} placeholder="KP" />
            </div>
          </NdtField>
          <NdtField label={lengthLabel('Diameter', units)}>
            <input className={NDT_INPUT} inputMode="decimal" value={v.diameter} onChange={set('diameter')} />
          </NdtField>
          <NdtField label={lengthLabel('Wall', units)}>
            <input className={NDT_INPUT} inputMode="decimal" value={v.wall} onChange={set('wall')} />
          </NdtField>
          <NdtField label="Material">
            <input className={NDT_INPUT} value={v.material} onChange={set('material')} />
          </NdtField>
          <CatalogSelect label="Process" value={v.process} onChange={set('process')} options={cat?.weldProcesses} blank="Not recorded" />
          <CatalogSelect label="Position" value={v.position} onChange={set('position')} options={cat?.weldPositions} blank="Not recorded" />
          <NdtField label="Welder" hint={welders.isError ? 'Welders could not be loaded.' : undefined}>
            <select className={NDT_INPUT} value={v.welder_id} onChange={set('welder_id')}>
              <option value="">{welders.isLoading ? 'Loading…' : 'Not recorded'}</option>
              {(welders.data?.data ?? []).map((w) => (
                <option key={w.id} value={w.id}>
                  {w.name}
                  {w.welderStamp ? ` (${w.welderStamp})` : ''}
                </option>
              ))}
            </select>
          </NdtField>
          <NdtField label="External weld ref">
            <input className={NDT_INPUT} value={v.external_weld_ref} onChange={set('external_weld_ref')} />
          </NdtField>
        </div>
        <NdtField label="Notes">
          <textarea className={`${NDT_INPUT} h-16 py-2`} value={v.notes} onChange={set('notes')} />
        </NdtField>
        <p className="text-xs text-fieldiq-muted">The welder&apos;s qualification is checked after saving. It warns, it does not stop the save.</p>
      </form>
    </NdtDialog>
  );
}

// ─── Exam ────────────────────────────────────────────────────────────────────

interface IndicationValues {
  key: number;
  indication_type: string;
  disposition: string;
  location: string;
  clock_position: string;
  length: string;
  depth: string;
}

interface ExamValues {
  method: string;
  cycle: string;
  technique: string;
  result: string;
  technician_id: string;
  instrument_equipment_id: string;
  source_id: string;
  repair_welder_id: string;
  shots_count: string;
  films_count: string;
  film_density: string;
  iqi: string;
  sensitivity_percent: string;
  examined_at: string;
  notes: string;
}

const RESULT_LABEL: Record<string, string> = { pending: 'Pending (shot, not read)', accept: 'Accept', repair: 'Repair', cutout: 'Cut out' };

/** Indications typed on the form → the API body. Throws a sentence on a bad figure. */
export function indicationBodies(rows: IndicationValues[]): NdtIndicationWrite[] {
  return rows.map((r, n) => {
    if (!r.indication_type) throw new Error(`Indication ${n + 1}: choose a type.`);
    if (r.disposition !== 'accept' && r.disposition !== 'reject') throw new Error(`Indication ${n + 1}: choose accept or reject.`);
    const location = numberFromInput(r.location);
    const length = numberFromInput(r.length);
    const depth = numberFromInput(r.depth);
    const clock = intFromInput(r.clock_position);
    if ([location, length, depth].some((x) => Number.isNaN(x))) throw new Error(`Indication ${n + 1}: location, length and depth must be numbers.`);
    if (Number.isNaN(clock) || (clock !== null && (clock < 1 || clock > 12))) throw new Error(`Indication ${n + 1}: clock position is a whole number from 1 to 12.`);
    return { indication_type: r.indication_type, disposition: r.disposition, location, length, depth, clock_position: clock };
  });
}

/**
 * Record an exam on a weld (POST /welds/:id/exams). `crew` limits the
 * technician picker to the job's crew (the shot guard refuses anyone else);
 * without it every person is offered and the API decides.
 */
export function NdtExamDialog({
  open,
  onClose,
  weldId,
  crew,
  meta,
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  weldId: string;
  crew?: NdtCrew[] | null;
  meta: NdtMeta | null | undefined;
  onSaved?: (warnings: string[]) => void;
}) {
  const add = useAddNdtExam();
  const catalog = useNdtCatalog();
  const cat = catalog.data?.data as Catalog;
  const people = useNdtPeople(open && !crew);
  const welders = useNdtWelders();
  const [refusal, setRefusal] = useState<NdtRefusal | null>(null);
  const [rows, setRows] = useState<IndicationValues[]>([]);
  const zone = meta?.timeZone ?? null;
  const units = meta?.units ?? null;
  const { v, set, setV, error, setError, start } = useForm<ExamValues>(open, () => ({
    method: '',
    cycle: '',
    technique: '',
    result: 'pending',
    technician_id: '',
    instrument_equipment_id: '',
    source_id: '',
    repair_welder_id: '',
    shots_count: '',
    films_count: '',
    film_density: '',
    iqi: '',
    sensitivity_percent: '',
    examined_at: toDatetimeLocal(new Date().toISOString(), zone),
    notes: '',
  }));
  React.useEffect(() => {
    if (open) {
      setRows([]);
      setRefusal(null);
    }
  }, [open]);

  const requiredKind = useMemo(() => {
    const kinds = cat?.examInstrumentKinds ?? {};
    if (v.method === 'RT') return v.source_id ? kinds.RT ?? null : null;
    return kinds[v.method] ?? null;
  }, [cat, v.method, v.source_id]);
  const instruments = useNdtInstruments(requiredKind ? { kind: requiredKind } : undefined);
  const sources = useNdtSources({ status: 'in_service' });
  const techniques = v.method ? cat?.techniques?.[v.method] : undefined;
  const isRt = v.method === 'RT';

  const onMethod = (e: Change) => {
    const method = e.target.value;
    setV((prev) => ({ ...prev, method, technique: '', instrument_equipment_id: '', source_id: method === 'RT' ? prev.source_id : '' }));
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setRefusal(null);
    if (!v.method) return setError('Choose a method.');
    if (!v.technician_id) return setError('Choose the technician.');
    const counts = [intFromInput(v.shots_count), intFromInput(v.films_count)];
    if (counts.some((x) => Number.isNaN(x))) return setError('Shots and films are whole numbers.');
    const density = numberFromInput(v.film_density);
    const sens = numberFromInput(v.sensitivity_percent);
    if (Number.isNaN(density) || Number.isNaN(sens)) return setError('Film density and sensitivity must be numbers.');
    let indications: NdtIndicationWrite[];
    try {
      indications = indicationBodies(rows);
    } catch (err) {
      return setError((err as Error).message);
    }
    const body: NdtExamWrite = {
      method: v.method,
      result: v.result as NdtExamWrite['result'],
      technician_id: v.technician_id,
      technique: v.technique || null,
      instrument_equipment_id: v.instrument_equipment_id || null,
      source_id: isRt && v.source_id ? v.source_id : null,
      repair_welder_id: v.repair_welder_id || null,
      shots_count: counts[0],
      films_count: counts[1],
      film_density: density,
      iqi: textOrNull(v.iqi),
      sensitivity_percent: sens,
      notes: textOrNull(v.notes),
    };
    if (v.cycle) body.cycle = v.cycle;
    if (indications.length) body.indications = indications;
    if (v.examined_at !== start.examined_at) {
      const at = fromDatetimeLocal(v.examined_at, zone);
      if (!at) return setError('The exam time could not be read. Pick a date and time.');
      body.examined_at = at;
    }
    add.mutate(
      { weldId, body },
      {
        onSuccess: (res) => {
          toast.success('Exam recorded');
          onClose();
          onSaved?.(res.warnings.map(ndtWarningText));
        },
        onError: (err) => setRefusal(ndtRefusal(err, 'The exam could not be recorded.')),
      },
    );
  };

  const techOptions = crew
    ? crew.map((m) => ({ id: m.userId, name: `${m.userName ?? '—'} · ${m.crewRoleLabel ?? m.crewRole}` }))
    : (people.data ?? []).map((u) => ({ id: u.id, name: ndtPersonName(u) }));
  const setRow = (key: number, k: keyof IndicationValues) => (e: Change) =>
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, [k]: e.target.value } : r)));

  return (
    <NdtDialog
      open={open}
      title="Record exam"
      onClose={onClose}
      wide
      footer={<Footer formId="ndt-exam" pending={add.isPending} label="Save exam" onClose={onClose} />}
    >
      <form id="ndt-exam" onSubmit={submit} className="space-y-4" noValidate>
        <NdtFormError message={error} />
        <NdtRefusalNotice refusal={refusal} />
        <div className="grid gap-3 sm:grid-cols-3">
          <CatalogSelect label="Method" value={v.method} onChange={onMethod} options={cat?.examMethods} blank="Choose a method" required />
          <NdtField label="Cycle" hint="Leave on Next and the API picks the right one.">
            <select className={NDT_INPUT} value={v.cycle} onChange={set('cycle')}>
              <option value="">Next</option>
              {Object.entries(NDT_EXAM_CYCLE_LABEL).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </NdtField>
          <CatalogSelect
            label="Technique"
            value={v.technique}
            onChange={set('technique')}
            options={techniques}
            blank={v.method ? 'Not recorded' : 'Choose a method first'}
          />
          <NdtField label="Result">
            <select className={NDT_INPUT} value={v.result} onChange={set('result')}>
              {Object.entries(RESULT_LABEL).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </NdtField>
          <NdtField label="Technician" hint={crew ? "The job's crew." : undefined}>
            <select className={NDT_INPUT} value={v.technician_id} onChange={set('technician_id')} required>
              <option value="">Choose the technician</option>
              {techOptions.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </NdtField>
          <NdtField label="Examined" hint={zone ? `On the company clock (${zone}).` : undefined}>
            <input type="datetime-local" className={NDT_DATE_INPUT} value={v.examined_at} onChange={set('examined_at')} disabled={!zone} />
          </NdtField>
          {isRt && (
            <NdtField label="Gamma source" hint="None for X-ray.">
              <select className={NDT_INPUT} value={v.source_id} onChange={set('source_id')}>
                <option value="">None (X-ray)</option>
                {(sources.data?.data ?? []).map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.serialNumber} · {s.isotopeLabel ?? s.isotope}
                  </option>
                ))}
              </select>
            </NdtField>
          )}
          <NdtField
            label="Instrument"
            hint={requiredKind ? `A ${ndtKindLabel(requiredKind).toLowerCase()} is required.` : v.method ? 'Optional for this method.' : undefined}
          >
            <select className={NDT_INPUT} value={v.instrument_equipment_id} onChange={set('instrument_equipment_id')}>
              <option value="">{instruments.isLoading ? 'Loading…' : 'None'}</option>
              {(instruments.data?.data ?? []).map((i) => (
                <option key={i.id} value={i.id}>
                  {i.name ?? '—'}
                  {i.serialNumber ? ` · ${i.serialNumber}` : ''}
                </option>
              ))}
            </select>
          </NdtField>
          <NdtField label="Repair welder" hint="On a repair or cut-out exam, if not the original welder.">
            <select className={NDT_INPUT} value={v.repair_welder_id} onChange={set('repair_welder_id')}>
              <option value="">Not recorded</option>
              {(welders.data?.data ?? []).map((w) => (
                <option key={w.id} value={w.id}>
                  {w.name}
                </option>
              ))}
            </select>
          </NdtField>
          {isRt && (
            <>
              <NdtField label="Shots">
                <input className={NDT_INPUT} inputMode="numeric" value={v.shots_count} onChange={set('shots_count')} />
              </NdtField>
              <NdtField label="Films">
                <input className={NDT_INPUT} inputMode="numeric" value={v.films_count} onChange={set('films_count')} />
              </NdtField>
              <NdtField label="Film density">
                <input className={NDT_INPUT} inputMode="decimal" value={v.film_density} onChange={set('film_density')} />
              </NdtField>
              <NdtField label="IQI">
                <input className={NDT_INPUT} value={v.iqi} onChange={set('iqi')} />
              </NdtField>
              <NdtField label="Sensitivity (%)">
                <input className={NDT_INPUT} inputMode="decimal" value={v.sensitivity_percent} onChange={set('sensitivity_percent')} />
              </NdtField>
            </>
          )}
        </div>

        <fieldset className="space-y-2">
          <legend className="text-[0.62rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">Indications</legend>
          {rows.length === 0 && <p className="text-xs text-fieldiq-muted">None recorded.</p>}
          {rows.map((r, n) => (
            <div key={r.key} data-testid="ndt-indication-row" className="grid gap-2 rounded-fi-control border border-fieldiq-border p-2 sm:grid-cols-7">
              <select aria-label={`Indication ${n + 1} type`} className={`${NDT_INPUT} sm:col-span-2`} value={r.indication_type} onChange={setRow(r.key, 'indication_type')}>
                <option value="">Type</option>
                {(cat?.indicationTypes ?? []).map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
              <select aria-label={`Indication ${n + 1} disposition`} className={NDT_INPUT} value={r.disposition} onChange={setRow(r.key, 'disposition')}>
                <option value="">Accept / reject</option>
                <option value="accept">Accept</option>
                <option value="reject">Reject</option>
              </select>
              <input aria-label={lengthLabel(`Indication ${n + 1} location`, units)} placeholder={lengthLabel('Location', units)} className={NDT_INPUT} inputMode="decimal" value={r.location} onChange={setRow(r.key, 'location')} />
              <input aria-label={`Indication ${n + 1} clock position`} placeholder="Clock 1–12" className={NDT_INPUT} inputMode="numeric" value={r.clock_position} onChange={setRow(r.key, 'clock_position')} />
              <input aria-label={lengthLabel(`Indication ${n + 1} length`, units)} placeholder={lengthLabel('Length', units)} className={NDT_INPUT} inputMode="decimal" value={r.length} onChange={setRow(r.key, 'length')} />
              <div className="flex gap-2">
                <input aria-label={lengthLabel(`Indication ${n + 1} depth`, units)} placeholder="Depth" className={NDT_INPUT} inputMode="decimal" value={r.depth} onChange={setRow(r.key, 'depth')} />
                <button type="button" className={NDT_SECONDARY} aria-label={`Remove indication ${n + 1}`} onClick={() => setRows((prev) => prev.filter((x) => x.key !== r.key))}>
                  <Trash2 className="size-4" aria-hidden="true" />
                </button>
              </div>
            </div>
          ))}
          <button
            type="button"
            className={NDT_SECONDARY}
            onClick={() =>
              setRows((prev) => [
                ...prev,
                { key: Date.now() + prev.length, indication_type: '', disposition: '', location: '', clock_position: '', length: '', depth: '' },
              ])
            }
          >
            <Plus className="size-4" aria-hidden="true" />
            Add indication
          </button>
        </fieldset>

        <NdtField label="Notes">
          <textarea className={`${NDT_INPUT} h-16 py-2`} value={v.notes} onChange={set('notes')} />
        </NdtField>
      </form>
    </NdtDialog>
  );
}
